// Armazenamento como o cliente vê (ADR 0021): volume contratado, usado e livre.
// Nada de backend, endereço, credencial ou botão de configuração: quem opera a
// infraestrutura é a plataforma.
import { Copy, Info, OctagonAlert, TriangleAlert } from "lucide-react";
import { Link, useParams } from "react-router";
import { useStorage } from "../api/queries";
import type { StorageSummary } from "../api/types";
import { useT } from "../i18n/core";
import { useFormat } from "../lib/format";
import { ErrorState, LoadingRows, PageHead, Panel } from "../ui/Common";
import { Meter } from "../ui/Meter";
import { Pill } from "../ui/Pills";
import { storageMessages } from "./storage.i18n";

/** Fração usada do contratado; nula quando não há limite. */
export function quotaRatio(s: Pick<StorageSummary, "quotaBytes" | "usedBytes">): number | null {
  return s.quotaBytes && s.quotaBytes > 0 ? s.usedBytes / s.quotaBytes : null;
}

/**
 * Aviso de quota: em 90% alerta, em 100% o gateway recusa blocos novos.
 * Usado na visão geral e no armazenamento; some quando não há limite.
 */
export function QuotaBanner({ summary, to }: { summary: StorageSummary | undefined; to?: string }) {
  const t = useT(storageMessages);
  const f = useFormat();
  const ratio = summary ? quotaRatio(summary) : null;
  if (!summary || ratio === null || ratio < 0.9) return null;
  const full = ratio >= 1;
  return (
    <div className={`callout ${full ? "crit" : "warn"}`} role={full ? "alert" : "status"}>
      {full ? <OctagonAlert aria-hidden="true" /> : <TriangleAlert aria-hidden="true" />}
      <div style={{ display: "grid", gap: 6, minWidth: 0 }}>
        <strong>{full ? t.fullTitle : t.nearTitle}</strong>
        <span>{full ? t.fullBody(f.pct(ratio)) : t.nearBody(f.pct(ratio), f.bytes(summary.freeBytes ?? 0))}</span>
        <span>{t.howToFree}</span>
        {to && <div><Link className="btn btn-sm" to={to}>{t.openStorage}</Link></div>}
      </div>
    </div>
  );
}

export function StoragePage() {
  const { orgId = "" } = useParams();
  const q = useStorage(orgId);
  const t = useT(storageMessages);
  const f = useFormat();

  if (q.isPending) return <><PageHead title={t.title} lede={t.lede} /><div className="panel"><LoadingRows rows={5} /></div></>;
  if (q.isError) return <><PageHead title={t.title} lede={t.lede} /><ErrorState error={q.error} /></>;

  const s = q.data;
  const ratio = quotaRatio(s);

  return (
    <>
      <PageHead title={t.title} lede={t.lede} />
      <QuotaBanner summary={s} />

      <Panel title={t.summaryTitle} sub={t.summarySub}>
        <div style={{ display: "grid", gap: 14 }}>
          <div className="stats">
            <div className="stat"><span className="v">{s.quotaBytes === null ? t.unlimited : f.bytes(s.quotaBytes)}</span><span className="k">{t.contracted}</span></div>
            <div className="stat"><span className="v">{f.bytes(s.usedBytes)}</span><span className="k">{t.used}</span></div>
            <div className="stat"><span className="v">{s.freeBytes === null ? t.unlimited : f.bytes(s.freeBytes)}</span><span className="k">{t.free}</span></div>
          </div>
          {s.quotaBytes !== null && ratio !== null && (
            <Meter value={s.usedBytes} max={s.quotaBytes} label={t.usedOf(f.bytes(s.usedBytes), f.bytes(s.quotaBytes))} detail={t.pctUsed(f.pct(ratio))} />
          )}
          <span className="hint">{t.usedHint}</span>
        </div>
      </Panel>

      <Panel title={t.areas} sub={t.areasSub} bodyless>
        {s.pools.length === 0 ? (
          <div className="empty">{t.noAreas}</div>
        ) : (
          <div className="pool-list">
            {s.pools.map((a) => (
              <div className="pool" key={a.id}>
                <div className="pool-top">
                  <h3>{a.name}</h3>
                  {a.replicated && <Pill tone="info" icon={Copy}>{t.replicated}</Pill>}
                </div>
                {a.quotaBytes === null ? (
                  <div className="meter-row"><span>{t.usedNoLimit(f.bytes(a.usedBytes))}</span></div>
                ) : (
                  <Meter
                    value={a.usedBytes}
                    max={a.quotaBytes}
                    label={t.usedOf(f.bytes(a.usedBytes), f.bytes(a.quotaBytes))}
                    detail={t.freeOf(f.bytes(a.freeBytes ?? 0))}
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </Panel>

      <Panel title={t.howTitle}>
        <ul className="consequences">
          <li>{t.how1}</li>
          <li>{t.how2}</li>
          <li>{t.how3}</li>
          <li>{t.how4}</li>
        </ul>
        <div className="callout" style={{ marginTop: 14 }}>
          <Info aria-hidden="true" />
          <span>{t.howToFree}</span>
        </div>
      </Panel>
    </>
  );
}
